import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { FileSearch, Plus } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { CheckRow } from '@/components/checks';
import { PageHeader } from '@/components/layout/AppShell';
import { Page, Rise } from '@/components/motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState, ErrorState, Pager, Skeleton, Tabs } from '@/components/ui/misc';
import { checksApi } from '@/lib/api/endpoints';
import type { CheckStatus } from '@/lib/api/types';
import { isInProgress } from '@/lib/visa';

export const CHECK_FILTERS: { value: CheckStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const LIMIT = 20;

export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="divide-y">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4 px-5 py-4">
          <Skeleton className="size-10 rounded-xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="w-40" />
            <Skeleton className="h-3 w-56" />
          </div>
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      ))}
    </div>
  );
}

export function ChecksPage() {
  const [status, setStatus] = React.useState<CheckStatus | 'all'>('all');
  const [offset, setOffset] = React.useState(0);
  const params = { status: status === 'all' ? undefined : status, limit: LIMIT, offset };
  const q = useQuery({
    queryKey: ['checks', params],
    queryFn: () => checksApi.list(params),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (query.state.data?.some((c) => isInProgress(c.status)) ? 5000 : false),
  });

  return (
    <Page>
      <PageHeader
        eyebrow="History"
        title="My checks"
        description="Every visa check you've run. Running checks update on their own."
        actions={
          <Button asChild variant="brand">
            <Link to="/checks/new">
              <Plus /> New check
            </Link>
          </Button>
        }
      />
      <Rise className="mb-4">
        <Tabs
          layoutId="checks-filter"
          value={status}
          onChange={(v) => {
            setStatus(v);
            setOffset(0);
          }}
          options={CHECK_FILTERS}
        />
      </Rise>
      <Rise>
        <Card className="overflow-hidden">
          {q.isPending ? (
            <ListSkeleton />
          ) : q.isError ? (
            <ErrorState error={q.error} onRetry={() => q.refetch()} />
          ) : q.data.length === 0 ? (
            <EmptyState
              icon={<FileSearch />}
              title={status === 'all' ? 'No checks yet' : 'Nothing here'}
              description={status === 'all' ? 'Start your first visa check.' : 'No checks with this status.'}
              action={
                status === 'all' ? (
                  <Button asChild variant="brand">
                    <Link to="/checks/new">
                      <Plus /> New check
                    </Link>
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="divide-y">
              {q.data.map((c) => (
                <CheckRow key={c.id} check={c} to={`/checks/${c.id}`} />
              ))}
            </div>
          )}
          <Pager offset={offset} limit={LIMIT} count={q.data?.length ?? 0} onChange={setOffset} loading={q.isFetching} />
        </Card>
      </Rise>
    </Page>
  );
}
